import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  ChevronRight,
  Layers,
  Loader2,
  MapPin,
  Network,
  Shield,
  Users,
} from "lucide-react";
import API from "../../services/api";
import { fetchOrgProfileStatus } from "../../services/orgProfileService";
import { fetchIamRoles } from "../../Moduels/IAM/iamApi";
import { listRoles } from "../../Moduels/Settings/RoleApi";

const DEFAULT_ROLE_LADDER = ["Super Admin", "HR", "Manager", "Employee"];

function activeRows(list) {
  if (!Array.isArray(list)) return [];
  return list.filter((r) => r.is_active !== false);
}

function normalizeRoles(raw) {
  const list = Array.isArray(raw) ? raw : raw?.roles || raw?.items || [];
  return list
    .map((r) => ({
      id: r.role_id ?? r.id,
      name: r.name || r.label || r.slug || "Role",
      slug: r.slug || r.code,
      parentId: r.parent_role_id ?? r.parent_id ?? null,
      order: r.hierarchy_order ?? r.sort_order ?? 999,
    }))
    .filter((r) => r.name);
}

function buildRoleLadder(roles) {
  if (!roles.length) return DEFAULT_ROLE_LADDER.map((name, i) => ({ name, level: i }));

  const byId = new Map(roles.map((r) => [r.id, r]));
  const roots = roles.filter((r) => !r.parentId || !byId.has(r.parentId));
  const ordered = (roots.length ? roots : [...roles]).sort(
    (a, b) => a.order - b.order || a.name.localeCompare(b.name)
  );

  return ordered.map((r, i) => ({ name: r.name, level: i }));
}

function previewNames(rows, max = 4) {
  const names = rows.map((r) => r.name || r.title).filter(Boolean);
  if (!names.length) return { items: [], more: 0 };
  if (names.length <= max) return { items: names, more: 0 };
  return { items: names.slice(0, max), more: names.length - max };
}

export default function OrganizationStructureHierarchy({ refreshToken = 0 }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [orgName, setOrgName] = useState("Your Organisation");
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [roleLadder, setRoleLadder] = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [profile, locRes, deptRes, desRes, iamRoles, legacyRoles] = await Promise.all([
          fetchOrgProfileStatus().catch(() => null),
          API.get("/setup/locations").catch(() => ({ data: [] })),
          API.get("/setup/departments").catch(() => ({ data: [] })),
          API.get("/setup/designations").catch(() => ({ data: [] })),
          fetchIamRoles().catch(() => null),
          listRoles().catch(() => []),
        ]);
        if (cancelled) return;

        const org = profile?.org;
        setOrgName(org?.name || org?.legal_name || "Your Organisation");
        setBranches(activeRows(locRes.data));
        setDepartments(activeRows(deptRes.data));
        setDesignations(activeRows(desRes.data));

        const normalized = normalizeRoles(iamRoles ?? legacyRoles);
        setRoleLadder(buildRoleLadder(normalized));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [refreshKey, refreshToken]);

  const branchPreview = useMemo(() => previewNames(branches), [branches]);
  const deptPreview = useMemo(() => previewNames(departments), [departments]);
  const desigPreview = useMemo(() => previewNames(designations), [designations]);

  const totalConfigured =
    branches.length + departments.length + designations.length;

  return (
    <section className="os-hierarchy" aria-label="Organisation structure overview">
      <div className="os-hierarchy-head">
        <div className="os-hierarchy-head-icon">
          <Network size={20} />
        </div>
        <div>
          <h2 className="os-hierarchy-title">Organisation map</h2>
          <p className="os-hierarchy-sub">
            Branches, departments, designations, and access roles — how your company is structured in Brixigo.
          </p>
        </div>
        {loading ? (
          <span className="os-hierarchy-loading">
            <Loader2 size={16} className="os-spin" aria-hidden /> Updating…
          </span>
        ) : (
          <div className="os-hierarchy-totals">
            <span className="os-hierarchy-total-pill">
              <strong>{totalConfigured}</strong> structure items
            </span>
            <span className="os-hierarchy-total-pill">
              <strong>{roleLadder.length || DEFAULT_ROLE_LADDER.length}</strong> access roles
            </span>
            <button
              type="button"
              className="os-hierarchy-refresh"
              onClick={() => setRefreshKey((k) => k + 1)}
              disabled={loading}
            >
              Refresh map
            </button>
          </div>
        )}
      </div>

      <div className="os-hierarchy-chart">
        <div className="os-hierarchy-root">
          <div className="os-hierarchy-node os-hierarchy-node--root">
            <Building2 size={22} aria-hidden />
            <span className="os-hierarchy-node-label">{orgName}</span>
            <span className="os-hierarchy-node-tag">Organisation</span>
          </div>
        </div>

        <div className="os-hierarchy-connector" aria-hidden />

        <div className="os-hierarchy-tier">
          <HierarchyColumn
            tone="emerald"
            icon={MapPin}
            title="Branches"
            count={branches.length}
            emptyHint="Add office locations & work sites"
            preview={branchPreview}
            loading={loading}
          />
          <HierarchyColumn
            tone="violet"
            icon={Layers}
            title="Departments"
            count={departments.length}
            emptyHint="Add teams & cost centres"
            preview={deptPreview}
            loading={loading}
          />
          <HierarchyColumn
            tone="amber"
            icon={Users}
            title="Designations"
            count={designations.length}
            emptyHint="Add job titles & grades"
            preview={desigPreview}
            loading={loading}
          />
        </div>

        <div className="os-hierarchy-connector os-hierarchy-connector--short" aria-hidden />

        <div className="os-hierarchy-roles">
          <div className="os-hierarchy-roles-head">
            <Shield size={18} aria-hidden />
            <div>
              <h3>Access role hierarchy</h3>
              <p>Highest privilege at the top — used for users, approvals, and security.</p>
            </div>
            <Link to="/settings/security/users-roles?tab=roles" className="os-hierarchy-roles-link">
              Manage roles <ChevronRight size={14} />
            </Link>
          </div>
          <ol className="os-hierarchy-ladder">
            {(roleLadder.length ? roleLadder : DEFAULT_ROLE_LADDER.map((name, level) => ({ name, level }))).map(
              (role, idx, arr) => (
                <li key={`${role.name}-${idx}`} className="os-hierarchy-ladder-step">
                  <span className="os-hierarchy-ladder-rank">{idx + 1}</span>
                  <span className="os-hierarchy-ladder-name">{role.name}</span>
                  {idx < arr.length - 1 ? <span className="os-hierarchy-ladder-arrow" aria-hidden /> : null}
                </li>
              )
            )}
          </ol>
        </div>
      </div>
    </section>
  );
}

function HierarchyColumn({ tone, icon: Icon, title, count, emptyHint, preview, loading }) {
  return (
    <article className={`os-hierarchy-col os-hierarchy-col--${tone}`}>
      <div className="os-hierarchy-col-head">
        <span className={`os-hierarchy-col-icon os-icon-tone--${tone}`}>
          <Icon size={17} />
        </span>
        <div>
          <h3>{title}</h3>
          <span className="os-hierarchy-col-count">
            {loading ? "—" : count} total
          </span>
        </div>
      </div>
      <ul className="os-hierarchy-col-list">
        {loading ? (
          <li className="os-hierarchy-col-empty">Loading…</li>
        ) : preview.items.length ? (
          <>
            {preview.items.map((name) => (
              <li key={name}>{name}</li>
            ))}
            {preview.more > 0 ? (
              <li className="os-hierarchy-col-more">+ {preview.more} more</li>
            ) : null}
          </>
        ) : (
          <li className="os-hierarchy-col-empty">{emptyHint}</li>
        )}
      </ul>
    </article>
  );
}
